/**
 * ProviderOSPayouts — earnings ledger + next payout + fast-pay toggle.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, Zap, CheckCircle2, Loader2, RefreshCw, ArrowRight, Inbox,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProviderOSGate from '../components/provider_os/ProviderOSGate';
import PayoutConnectBanner from '../components/provider_os/PayoutConnectBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatUsd = (cents) => {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const StatusPill = ({ status }) => {
  const map = {
    pending:   { tone: 'bg-slate-100 text-slate-700',     label: 'Pending' },
    ready:     { tone: 'bg-amber-100 text-amber-800',     label: 'Ready' },
    paid:      { tone: 'bg-emerald-100 text-emerald-800', label: 'Paid' },
    failed:    { tone: 'bg-red-100 text-red-800',         label: 'Failed' },
    cancelled: { tone: 'bg-slate-200 text-slate-600',     label: 'Cancelled' },
  };
  const m = map[status] || { tone: 'bg-slate-100 text-slate-700', label: status };
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${m.tone}`}>
      {m.label}
    </span>
  );
};

export default function ProviderOSPayouts() {
  return (
    <ProviderOSGate>
      <PayoutsInner />
    </ProviderOSGate>
  );
}

function PayoutsInner() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/provider/payouts` + (filter !== 'all' ? `?status=${filter}` : '');
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/provider/payouts/reconcile`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reconciled ${res.data.reconciled} payout${res.data.reconciled === 1 ? '' : 's'}`);
      load();
    } catch (e) {
      toast.error('Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  const doAction = async (id, action, body) => {
    try {
      await axios.post(`${API}/provider/payouts/${id}/${action}`,
        body || {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${action} ✓`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Action failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  // KPIs
  const totals = rows.reduce((acc, p) => {
    if (p.status === 'pending') acc.pending += p.net_cents;
    else if (p.status === 'ready') acc.ready += p.net_cents;
    else if (p.status === 'paid') acc.paid += p.net_cents;
    return acc;
  }, { pending: 0, ready: 0, paid: 0 });

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="provider-os-payouts">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Earnings ledger</h1>
          <p className="text-sm text-slate-500 mt-1">
            Per-placement payouts. Reconcile pending rows once visits are committed.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/provider-os" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
            Dashboard <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={reconcile}
            disabled={reconciling}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
            data-testid="reconcile-btn"
          >
            {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Reconcile
          </button>
        </div>
      </div>

      {/* Iter 161 · Stripe Connect status + Instant Payout CTA. Renders
          nothing for non-clinician callers or when the banking endpoint
          errors, so agencies/planners on this page don't see a broken tile. */}
      <PayoutConnectBanner onPayoutSuccess={load} />

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="kpi-pending">
          <div className="text-xs uppercase tracking-wide text-slate-500">Pending</div>
          <div className="text-2xl font-semibold text-slate-900 mt-1">{formatUsd(totals.pending)}</div>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="kpi-ready">
          <div className="text-xs uppercase tracking-wide text-amber-700">Ready to settle</div>
          <div className="text-2xl font-semibold text-amber-900 mt-1">{formatUsd(totals.ready)}</div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="kpi-paid">
          <div className="text-xs uppercase tracking-wide text-emerald-700">Paid</div>
          <div className="text-2xl font-semibold text-emerald-900 mt-1">{formatUsd(totals.paid)}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'ready', 'paid', 'failed', 'cancelled'].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === s ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`pay-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No payouts in this view yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="payouts-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">Placement</th>
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Fast-pay fee</th>
                <th className="px-4 py-3 text-right">Net</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                    data-testid={`pay-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">
                    {p.placement_id.slice(0, 8)}…
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{formatUsd(p.amount_cents)}</td>
                  <td className="px-4 py-3 text-right text-slate-700">
                    {p.fast_pay ? (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <Zap className="w-3 h-3" /> {formatUsd(p.fast_pay_fee_cents)}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">
                    {formatUsd(p.net_cents)}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <PayoutActions p={p} onAction={doAction} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const PayoutActions = ({ p, onAction }) => {
  if (p.status === 'pending' || p.status === 'ready') {
    return (
      <div className="inline-flex gap-2 justify-end">
        <button
          onClick={() => onAction(p.id, 'fast-pay')}
          className="text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200"
          data-testid={`fast-pay-btn-${p.id}`}
        >
          <Zap className="w-3 h-3 inline mr-1" />
          {p.fast_pay ? 'Disable fast' : 'Enable fast'}
        </button>
        {p.status === 'ready' && (
          <button
            onClick={() => {
              const ref = prompt('Settlement reference (e.g. Stripe tx id):');
              if (ref !== null) onAction(p.id, 'settle', { settlement_ref: ref || null });
            }}
            className="text-xs px-3 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid={`settle-btn-${p.id}`}
          >
            <CheckCircle2 className="w-3 h-3 inline mr-1" /> Settle
          </button>
        )}
      </div>
    );
  }
  return <span className="text-xs text-slate-400">—</span>;
};
